import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Crown, Check, Sparkles, Loader2, ArrowLeft, AlertCircle, Gift } from 'lucide-react';
import { PREMIUM_AI_POINTS } from '../lib/pricing';
import { formatPrice, loadWebPrices, pricingParams } from '../lib/webPrices';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n';
import { toast } from 'sonner';
import { buyWithGooglePlay, loadPlayPrices, restoreGooglePlay } from '../lib/playBilling';
import GrowGoldShop from '../components/GrowGoldShop';
import AiHistory from '../components/AiHistory';

// Web prices come from the configured PayPal plan. Android displays only
// Google ProductDetails prices; never substitute web prices for Play prices.

export default function Premium() {
    const { user, refresh } = useAuth();
    const { t, locale } = useI18n();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [busy, setBusy] = useState(false);
    const [billing, setBilling] = useState('monthly');
    const [paypalCfg, setPaypalCfg] = useState({ configured: false, mode: 'sandbox', price_monthly: '', price_yearly: '', currency: '', trial_days: 0 });
    const [playPrices, setPlayPrices] = useState({});
    const [growgoldOpen, setGrowgoldOpen] = useState(false);
    const [catalog, setCatalog] = useState(null);
    const [catalogError, setCatalogError] = useState(false);
    const [playError, setPlayError] = useState('');
    const [restoring, setRestoring] = useState(false);
    const [accountStale, setAccountStale] = useState(false);
    const [creditStatus, setCreditStatus] = useState(null);
    const [creditBusy, setCreditBusy] = useState('');
    const [creditStale, setCreditStale] = useState(false);
    const onSuccess = window.location.pathname.endsWith('/success');
    const paypalErr = params.get('paypal_error');
    const creditResult = params.get('credits');
    const native = !!(window.Capacitor?.isNativePlatform?.());
    const da = locale === 'da';
    // The owned entitlement is account-wide, not the offer sold on this device.
    // In particular, opening the website must not relabel Play Plus as legacy
    // unlimited Premium or offer a second PayPal subscription.
    const ownsPlus = !!(user?.premium && user?.premium_via_plus);
    const plus = ownsPlus || (native && growgoldOpen);
    const allowanceUnits = user?.premium_allowance?.limit ?? catalog?.premium_proposal?.monthly_ai_units ?? 150;
    const userId = user?.user_id;
    const isGuest = user?.is_guest;
    const planKey = (period) => growgoldOpen ? (period === 'yearly' ? 'plusYearly' : 'plusMonthly') : period;
    useEffect(() => {
        let active = true;
        api.get('/play-billing/catalog').then(r => {
            if (typeof r.data?.growgold_enabled !== 'boolean') throw new Error('Invalid billing catalog');
            if (active) { setCatalog(r.data); setGrowgoldOpen(r.data.growgold_enabled); }
        }).catch(() => { if (active) setCatalogError(true); });
        return () => { active = false; };
    }, []);
    useEffect(() => {
        if (!native) return;
        let active = true;
        loadPlayPrices().then(prices => { if (active) setPlayPrices(prices); })
            .catch(() => { if (active) setPlayError('Could not load Google Play prices. Please try again later.'); });
        return () => { active = false; };
    }, [native]);
    const restorePurchases = async () => {
        if (!user || user.is_guest) { navigate('/register?next=/premium'); return; }
        setRestoring(true);
        try {
            const result = await restoreGooglePlay();
            try { setAccountStale(await refresh({ preserveOnError: true }) === false); } catch { setAccountStale(true); }
            // A legacy balance refresh must not turn a verified Play restore
            // into a reported payment failure.
            try { setCreditStatus((await api.get('/paypal/credits', priceParams)).data); } catch {}
            toast[result.failed ? 'error' : 'success'](
                result.failed ? 'Some purchases could not be restored. Please retry or contact support.'
                : result.pending ? 'Some payments are still pending with Google.'
                : 'Purchases checked. Your account is up to date.');
        } catch (err) { toast.error(err?.message || 'Could not restore purchases.'); }
        finally { setRestoring(false); }
    };

    // Arithmetic below is only shown for the web provider's quoted plan.
    // Amounts and currency are exactly what the server will charge.
    const monthlyAmount = paypalCfg.price_monthly;
    const yearlyAmount = paypalCfg.price_yearly;
    const money = (amount) => formatPrice(amount, paypalCfg.currency, locale) || '…';
    const yearlyMonthEq = (parseFloat(yearlyAmount) / 12).toFixed(2);
    const yearSavings = (parseFloat(monthlyAmount) * 12 - parseFloat(yearlyAmount)).toFixed(2);
    const savePct = Math.round(
        (1 - (parseFloat(yearlyAmount) / 12) / parseFloat(monthlyAmount)) * 100
    ) || 0;
    const priceParams = { params: pricingParams(locale) };

    useEffect(() => {
        if (native) return;
        let live = true;
        loadWebPrices(locale).then(p => { if (live && p) setPaypalCfg(p.config); });
        return () => { live = false; };
    }, [native, locale]);

    useEffect(() => {
        if (!userId || isGuest) return;
        api.get('/paypal/credits', { params: pricingParams(locale) }).then(r => setCreditStatus(r.data)).catch(() => {});
    }, [userId, isGuest, locale]);

    useEffect(() => {
        if (creditResult === 'paid') {
            toast.success('AI credits added to your account.');
            api.get('/paypal/credits', { params: pricingParams(locale) }).then(r => setCreditStatus(r.data)).catch(() => {});
        } else if (creditResult === 'failed') toast.error('The AI-credit payment could not be verified.');
    }, [creditResult, locale]);

    useEffect(() => {
        if (!onSuccess) return;
        // Coming back from PayPal — refresh user to pick up new premium status
        let attempts = 0;
        const poll = async () => {
            attempts++;
            try {
                await refresh();
            } catch {}
            if (attempts < 4) setTimeout(poll, 1500);
        };
        poll();
    }, [onSuccess, refresh]);

    const startPaypal = async () => {
        // Anonymous or guest → create a real account first, then come back here
        if (!user || user.is_guest) {
            navigate('/register?next=/premium');
            return;
        }
        setBusy(true);
        try {
            const { getFingerprint } = await import('../lib/fingerprint');
            const r = await api.post('/paypal/subscribe', {
                origin_url: window.location.origin,
                billing,
                currency: paypalCfg.currency,
                fingerprint: getFingerprint(),
            });
            if (r.data?.approve_url) {
                window.location.href = r.data.approve_url;
                return;
            }
            toast.error(t('premium.checkout_fail'));
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === 'string' ? d : t('premium.checkout_fail'));
        } finally { setBusy(false); }
    };

    const startCreditPack = async (pack) => {
        if (native && (!catalog || growgoldOpen)) return;
        if (!user || user.is_guest) { navigate('/register?next=/premium'); return; }
        // Google Play Billing will own consumable purchases in the Android
        // release. Do not expose a browser payment escape hatch there.
        if (native) {
            setCreditBusy(pack);
            try {
                await buyWithGooglePlay(pack, 'inapp');
                toast.success('AI credits added to your account.');
                try { setCreditStatus((await api.get('/paypal/credits', priceParams)).data); setCreditStale(false); }
                catch { setCreditStatus(null); setCreditStale(true); }
            } catch (err) {
                toast.error(err?.response?.data?.detail || err?.message || 'Could not complete Google Play purchase.');
            } finally { setCreditBusy(''); }
            return;
        }
        setCreditBusy(pack);
        try {
            const response = await api.post('/paypal/credit-packs/order', {
                pack, origin_url: window.location.origin, currency: paypalCfg.currency,
            });
            if (response.data?.approve_url) { window.location.href = response.data.approve_url; return; }
            toast.error('Could not open secure checkout.');
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not open secure checkout.');
        } finally { setCreditBusy(''); }
    };

    const startNativePremium = async () => {
        // Never fall back to a legacy product while the release catalog is
        // loading or unavailable, even if its Google price arrived first.
        if (!catalog) return;
        if (!user || user.is_guest) { navigate('/register?next=/premium'); return; }
        setBusy(true);
        try {
            await buyWithGooglePlay(planKey(billing), 'subs');
            try { setAccountStale(await refresh({ preserveOnError: true }) === false); } catch { setAccountStale(true); }
            toast.success('Premium is active. Thank you for supporting GrowDaily!');
        } catch (err) {
            toast.error(err?.response?.data?.detail || err?.message || 'Could not complete Google Play purchase.');
        } finally { setBusy(false); }
    };

    if (onSuccess) {
        return (
            <div className="px-5 pt-20 pb-32 max-w-md mx-auto text-center" data-testid="premium-success">
                <div className="text-6xl mb-4">🌱</div>
                <h1 className="font-serif text-3xl text-brand-ink">{t('premium.thank_you')}</h1>
                <p className="text-brand-ink2 mt-2">{t('premium.success_sub')}</p>
                {user?.premium && (
                    <div className="mt-4 text-xs text-brand-green inline-flex items-center gap-1">
                        <Check size={14} /> {t('premium.active')}
                    </div>
                )}
                <button onClick={() => navigate('/profile')} className="mt-6 bg-brand-green text-white px-6 py-3 rounded-full" data-testid="btn-to-profile">{t('premium.to_profile')}</button>
            </div>
        );
    }

    return (
        <div className="px-5 pt-10 pb-32 max-w-2xl mx-auto">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-brand-ink3 hover:text-brand-ink mb-6"><ArrowLeft size={14} /> {t('premium.back')}</button>

            <div className="bg-gradient-to-br from-brand-ochre/15 to-brand-terracotta/15 border border-brand-ochre/30 rounded-3xl p-8 mb-6">
                <Crown size={28} strokeWidth={1.8} className="text-brand-ochre mb-4" />
                <h1 className="font-serif text-4xl text-brand-ink leading-tight">{plus ? 'GrowDaily Premium Plus' : 'GrowDaily Premium'}</h1>
                <p className="text-brand-ink2 mt-2">{t('premium.hero_sub')}</p>
                {!ownsPlus && !native && paypalCfg.trial_days > 0 && (
                    <div className="mt-3 inline-flex items-center gap-1 bg-brand-green/15 text-brand-green text-xs font-medium px-3 py-1 rounded-full" data-testid="trial-badge">
                        🎁 {paypalCfg.trial_days}-{t('premium.trial_badge')}
                    </div>
                )}
            </div>

            {ownsPlus && <section className="mb-6 rounded-3xl border border-brand-green/30 bg-brand-green/10 p-5 text-sm text-brand-ink2" data-testid="owned-plus">
                <h2 className="font-serif text-xl text-brand-ink mb-2">{da ? 'Dit Premium Plus' : 'Your Premium Plus'}</h2>
                <p>{da ? 'Dit Google Play-abonnement følger din GrowDaily-konto på både telefon og web. Køb ikke et ekstra webabonnement.' : 'Your Google Play subscription follows your GrowDaily account on phone and web. Do not buy an extra web subscription.'}</p>
                {Number.isInteger(user?.premium_allowance?.remaining) && <p className="mt-2">{da
                    ? `${user.premium_allowance.remaining} af ${allowanceUnits} AI-enheder tilbage i denne månedspulje.`
                    : `${user.premium_allowance.remaining} of ${allowanceUnits} AI units left in this monthly allowance.`}</p>}
                <a className="inline-block mt-3 underline text-brand-green" href="https://play.google.com/store/account/subscriptions?package=fun.growdaily.app">{da ? 'Administrer i Google Play' : 'Manage in Google Play'}</a>
            </section>}
            {user?.premium && !ownsPlus && <p className="mb-6 text-sm text-brand-ink2" data-testid="owned-legacy">{da
                ? 'Din eksisterende Premium-adgang bevares. Den bliver ikke automatisk ændret til Premium Plus, og købt GrowGold er en separat saldo.'
                : 'Your existing Premium access is preserved. It is not automatically converted to Premium Plus, and purchased GrowGold is a separate balance.'}</p>}

            {/* Only show new plan prices when not describing an owned Plus plan. */}
            {!ownsPlus && <div className="bg-brand-card border border-brand-border rounded-3xl p-2 mb-3 grid grid-cols-2 gap-2" data-testid="billing-toggle">
                <button
                    data-testid="btn-billing-monthly"
                    onClick={() => setBilling('monthly')}
                    className={`p-4 rounded-2xl text-left transition-colors ${billing === 'monthly' ? 'bg-brand-green text-white' : 'bg-brand-bg2 text-brand-ink hover:bg-brand-bg'}`}
                >
                    <div className="text-xs uppercase tracking-widest font-semibold opacity-70">{t('premium.monthly')}</div>
                    <div className="font-serif text-2xl mt-1">{native ? (catalog ? playPrices[planKey('monthly')]?.price || 'Unavailable' : '…') : money(monthlyAmount)}</div>
                    <div className="text-xs opacity-80">{t('premium.billed_monthly')}</div>
                </button>
                <button
                    data-testid="btn-billing-yearly"
                    onClick={() => setBilling('yearly')}
                    className={`relative p-4 rounded-2xl text-left transition-colors ${billing === 'yearly' ? 'bg-brand-green text-white' : 'bg-brand-bg2 text-brand-ink hover:bg-brand-bg'}`}
                >
                    {!native && <div className="absolute top-2 right-2 bg-brand-ochre text-white text-[9px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full">{t('premium.save')} {savePct}%</div>}
                    <div className="text-xs uppercase tracking-widest font-semibold opacity-70">{t('premium.yearly_label')}</div>
                    <div className="font-serif text-2xl mt-1">{native ? (catalog ? playPrices[planKey('yearly')]?.price || 'Unavailable' : '…') : money(yearlyAmount)}</div>
                    <div className="text-xs opacity-80">
                        {t('premium.billed_yearly')}{!native && <> · {money(yearlyMonthEq)}{t('premium.per_mo')}</>}
                    </div>
                    {!native && <div className="text-[11px] font-semibold mt-1 opacity-90">
                        {t('premium.year_save')} {money(yearSavings)} {t('premium.year_save_suffix')}
                    </div>}
                </button>
            </div>}
            <div className="mb-6" />

            <h2 className="font-serif text-lg text-brand-ink mb-3">{t('premium.what_you_get')}</h2>
            {plus && <p className="mb-4 text-sm text-brand-ink2">{da
                ? `Premium Plus: reklamefri GrowDaily og Eden, havefordele og ${allowanceUnits} AI-enheder hver måned. Enheder er ikke antal svar: prisen afhænger af funktionen og antal billeder. Puljen fornyes månedligt uden overførsel, også ved årsbetaling. Købt GrowGold udløber ikke og bruges kun efter din prisbekræftelse.`
                : `Premium Plus: ad-free GrowDaily and Eden, gardening benefits and ${allowanceUnits} AI units each month. Units are not a number of answers: cost depends on the feature and number of images. Allowance resets monthly without carry-over, including annual plans. Purchased GrowGold does not expire and is spent only after your price confirmation.`}</p>}
            <ul className="space-y-2.5 mb-8">
                {(native && !catalog && !ownsPlus ? [] : plus ? (da ? [
                    'Reklamefri GrowDaily og Eden.',
                    'Tilføj mere end gratisplanens 10 planter til din have.',
                    'Vedhæft op til 18 billeder, når du deler et bed i fællesskabet.',
                    `${allowanceUnits} AI-enheder pr. måned — ikke ubegrænset AI.`,
                    'Den præcise pris og fordeling mellem månedspulje og GrowGold vises før hvert betalt AI-kald.',
                    'Købt GrowGold er valgfrit, uden abonnement og uden automatisk genopfyldning.',
                ] : [
                    'Ad-free GrowDaily and Eden.',
                    'Add more than the free plan’s 10 plants to your garden.',
                    'Attach up to 18 photos when sharing a bed with the community.',
                    `${allowanceUnits} AI units per month — not unlimited AI.`,
                    'The exact cost and split between your allowance and GrowGold appear before each paid AI request.',
                    'Purchased GrowGold is optional, with no subscription or automatic top-up.',
                ]) : [
                    da ? `${PREMIUM_AI_POINTS} AI-point hver måned til alle AI-funktioner (fx ${PREMIUM_AI_POINTS / 5} svar i chatten eller ${PREMIUM_AI_POINTS / 10} foto-analyser).`
                        : `${PREMIUM_AI_POINTS} AI points each month for every AI feature (e.g. ${PREMIUM_AI_POINTS / 5} chat answers or ${PREMIUM_AI_POINTS / 10} photo analyses).`,
                    t('premium.feat1'),
                    t('premium.feat2'),
                    t('premium.feat3'),
                    t('premium.feat4'),
                    t('premium.feat5'),
                    t('premium.feat6'),
                    t('premium.feat7'),
                ]).map((line) => (
                    <li key={line} className="flex items-start gap-3 bg-brand-card border border-brand-border rounded-2xl p-3">
                        <Check size={16} className="text-brand-green flex-shrink-0 mt-0.5" strokeWidth={2.2} />
                        <span className="text-sm text-brand-ink">{line}</span>
                    </li>
                ))}
            </ul>

            {!user?.premium && (!native || (catalog && !growgoldOpen)) && (
                <section className="mb-8 bg-brand-card border border-brand-border rounded-3xl p-5" data-testid="ai-credit-packs">
                    <div className="flex items-start justify-between gap-4 mb-2">
                        <div>
                            <h2 className="font-serif text-lg text-brand-ink">No subscription? Use AI when you need it.</h2>
                            <p className="text-xs text-brand-ink2 mt-1">Each credit pays for one plant ID or photo diagnosis. Credits do not expire.</p>
                        </div>
                        <Sparkles size={20} className="text-brand-ochre shrink-0" />
                    </div>
                    {creditStatus && <p className="text-xs text-brand-green mb-4" data-testid="ai-credit-balance">
                        {creditStatus.free_remaining}/{creditStatus.free_monthly_limit} free this month · {creditStatus.ai_credits} purchased credits
                    </p>}
                    {creditStale && <p role="status">{da ? 'Købet er bekræftet, men credit-saldoen kunne ikke opdateres. Genåbn siden; køb ikke igen for at gendanne saldoen.' : 'Purchase confirmed, but the credit balance could not refresh. Reopen this page; do not buy again to recover the balance.'}</p>}
                    <div className="grid sm:grid-cols-2 gap-3">
                            {(creditStatus?.packs || []).map((pack) => (
                                <button key={pack.key} onClick={() => startCreditPack(pack.key)} disabled={!!creditBusy || creditStale || (native ? !playPrices[pack.key]?.price : !paypalCfg.configured || !paypalCfg.currency)}
                                    className="text-left border border-brand-border hover:border-brand-green rounded-2xl p-4 transition-colors disabled:opacity-50">
                                    <div className="font-semibold text-brand-ink">{pack.credits} AI credits</div>
                                    <div className="text-xs text-brand-ink2 mt-1">One-time purchase · no subscription{native ? ' · Google Play' : ''}</div>
                                    <div className="text-brand-green font-serif text-xl mt-3">{native ? playPrices[pack.key]?.price || 'Unavailable' : formatPrice(pack.price, pack.currency, locale)}</div>
                                    {creditBusy === pack.key && <div className="text-xs mt-1 text-brand-ink2">Opening secure checkout…</div>}
                                </button>
                            ))}
                    </div>
                </section>
            )}

            {plus && creditStatus?.ai_credits > 0 && <p className="mb-6 text-sm text-brand-ink2" data-testid="legacy-credit-balance">
                {da ? `Dine ${creditStatus.ai_credits} tidligere købte AI-credits bevares til de funktioner, de blev købt til. De er separate fra GrowGold.`
                    : `Your ${creditStatus.ai_credits} previously purchased AI credits remain available for their original features. They are separate from GrowGold.`}
            </p>}

            <GrowGoldShop native={native} user={user} prices={playPrices} />
            <AiHistory user={user} />

            {native && <section className="mb-6 space-y-3 text-sm">
                {accountStale && <p role="status">{da ? 'Købet er kontrolleret, men profilvisningen kunne ikke opdateres. Genåbn siden; køb ikke igen for at opdatere profilen.' : 'The purchase was checked, but your profile could not refresh. Reopen this page; do not buy again to refresh your profile.'}</p>}
                {!catalog && <p role={catalogError ? 'alert' : 'status'}>{catalogError
                    ? (da ? 'Købsopsætningen kunne ikke hentes. Genåbn siden for at prøve igen; ingen betaling er startet.' : 'Could not load purchase configuration. Reopen this page to retry; no payment has started.')
                    : (da ? 'Henter købsopsætning…' : 'Loading purchase configuration…')}</p>}
                {playError && <p role="alert" className="text-brand-terracotta">{playError}</p>}
                <button onClick={restorePurchases} disabled={restoring} className="border border-brand-border rounded-full px-5 py-3">
                    {restoring ? 'Checking purchases…' : 'Restore purchases'}
                </button>
                <p><a href="https://play.google.com/store/account/subscriptions?package=fun.growdaily.app" className="underline">Manage Google Play subscription</a></p>
                <p className="text-brand-ink2">{da ? 'Abonnement er valgfrit. Det fornyes automatisk, indtil du opsiger i Google Play. Afinstallation eller kontosletning opsiger ikke abonnementet.' : 'A subscription is optional. It renews automatically until cancelled in Google Play. Uninstalling or deleting your account does not cancel it.'}</p>
            </section>}

            <div className="mb-4 space-y-2 text-sm text-brand-ink2" data-testid="purchase-disclosure">
                {!ownsPlus && native && catalog && playPrices[planKey(billing)]?.price && <p>{da
                    ? `${playPrices[planKey(billing)].price} ${billing === 'yearly' ? 'pr. år, betalt samlet én gang årligt' : 'pr. måned'}, indtil opsigelse. Bekræft den endelige pris i Google Play.`
                    : `${playPrices[planKey(billing)].price} ${billing === 'yearly' ? 'per year, charged in full once a year' : 'per month'}, until cancelled. Confirm the final price in Google Play.`}</p>}
                <p><a href="/purchase-terms" className="underline text-brand-green">{da ? 'Købsbetingelser og refusion' : 'Purchase terms and refunds'}</a> · <a href="/privacy" className="underline text-brand-green">{da ? 'Privatlivspolitik' : 'Privacy policy'}</a></p>
            </div>

            {paypalErr && (
                <div className="mb-4 flex items-start gap-2 text-sm text-brand-terracotta bg-brand-terracotta/10 border border-brand-terracotta/20 rounded-2xl p-3" data-testid="paypal-error">
                    <AlertCircle size={16} strokeWidth={1.8} className="flex-shrink-0 mt-0.5" />
                    <span>{t('premium.pay_not_approved')}</span>
                </div>
            )}

            {paypalCfg.trial_eligible === false && (
                <div className="mb-4 text-xs text-brand-ink3 text-center" data-testid="trial-ineligible-note">
                    {t('premium.trial_ineligible')}
                </div>
            )}

            {!user?.premium && !native && paypalCfg.trial_days > 0 && paypalCfg.trial_eligible !== false && (
                <div className="mb-4 flex items-center justify-center gap-2 text-sm text-brand-green bg-brand-green/10 border border-brand-green/25 rounded-2xl py-3 px-4 font-medium text-center" data-testid="trial-highlight">
                    <Gift size={16} strokeWidth={1.9} className="flex-shrink-0" />
                    <span>
                        <strong>{paypalCfg.trial_days} {t('premium.trial_highlight')}</strong>{' '}
                        {money(billing === 'yearly' ? yearlyAmount : monthlyAmount)}{billing === 'yearly' ? t('premium.per_yr') : t('premium.per_mo')}. {t('premium.cancel_anytime')}
                    </span>
                </div>
            )}

            <button
                data-testid="btn-paypal-checkout"
                onClick={native ? startNativePremium : startPaypal}
                disabled={busy || accountStale || user?.premium || (native && (!catalog || !playPrices[planKey(billing)]?.price)) || (!native && (!paypalCfg.configured || !paypalCfg.currency) && !!user && !user.is_guest)}
                className={`w-full ${native ? 'bg-brand-green hover:bg-brand-ink' : 'bg-[#0070ba] hover:bg-[#005ea6]'} text-white py-4 rounded-full font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50 transition-colors`}
            >
                {user?.premium ? t('premium.already') : busy ? <><Loader2 size={15} className="animate-spin" /> {t('premium.opening')}</> : <>
                    {native ? <>Google Play · {billing === 'yearly' ? t('premium.yearly_label') : t('premium.monthly')}</> : <><PayPalLogo /> {paypalCfg.trial_days > 0 ? `${t('premium.start_trial')} ${paypalCfg.trial_days}-${t('premium.trial_badge')}` : t('premium.subscribe_paypal')}</>}
                </>}
            </button>

            {!native && !paypalCfg.configured && !user?.premium && (
                <div className="mt-3 text-center text-xs text-brand-ink3" data-testid="paypal-not-configured">
                    {t('premium.not_configured')}
                </div>
            )}

            {!ownsPlus && <p className="text-xs text-brand-ink3 mt-4 text-center">
                {native ? 'Secure checkout through Google Play.' : <>{t('premium.secure')}{paypalCfg.mode === 'sandbox' ? ' (Sandbox)' : ''}</>}
            </p>}
        </div>
    );
}

function PayPalLogo() {
    return (
        <svg width="56" height="14" viewBox="0 0 124 33" fill="currentColor" aria-hidden="true">
            <path d="M46.211 6.749h-6.839a.95.95 0 0 0-.939.802l-2.766 17.537a.57.57 0 0 0 .564.658h3.265a.95.95 0 0 0 .939-.803l.746-4.73a.95.95 0 0 1 .938-.803h2.165c4.505 0 7.105-2.18 7.784-6.5.306-1.89.013-3.375-.872-4.415-.972-1.143-2.696-1.746-4.985-1.746zM47 13.154c-.374 2.454-2.249 2.454-4.062 2.454h-1.032l.724-4.583a.57.57 0 0 1 .563-.481h.473c1.235 0 2.4 0 3.002.704.359.42.469 1.044.332 1.906zM66.654 13.075h-3.275a.57.57 0 0 0-.563.481l-.145.916-.229-.332c-.709-1.029-2.29-1.373-3.868-1.373-3.619 0-6.71 2.741-7.312 6.586-.313 1.918.132 3.752 1.22 5.031.998 1.176 2.426 1.666 4.125 1.666 2.916 0 4.533-1.875 4.533-1.875l-.146.91a.57.57 0 0 0 .562.66h2.95a.95.95 0 0 0 .939-.803l1.77-11.209a.568.568 0 0 0-.561-.658zm-4.565 6.374c-.316 1.871-1.801 3.127-3.695 3.127-.951 0-1.711-.305-2.199-.883-.484-.574-.668-1.391-.514-2.301.295-1.855 1.805-3.152 3.67-3.152.93 0 1.686.309 2.184.892.499.589.697 1.411.554 2.317zM84.096 13.075h-3.291a.954.954 0 0 0-.787.417l-4.539 6.686-1.924-6.425a.953.953 0 0 0-.912-.678h-3.234a.57.57 0 0 0-.541.754l3.625 10.638-3.408 4.811a.57.57 0 0 0 .465.9h3.287a.949.949 0 0 0 .781-.408l10.946-15.8a.57.57 0 0 0-.468-.895z"/>
            <path d="M94.992 6.749h-6.84a.95.95 0 0 0-.938.802l-2.766 17.537a.569.569 0 0 0 .562.658h3.51a.665.665 0 0 0 .656-.562l.785-4.971a.95.95 0 0 1 .938-.803h2.164c4.506 0 7.105-2.18 7.785-6.5.307-1.89.012-3.375-.873-4.415-.971-1.143-2.694-1.746-4.983-1.746zm.789 6.405c-.373 2.454-2.248 2.454-4.062 2.454h-1.031l.725-4.583a.568.568 0 0 1 .562-.481h.473c1.234 0 2.4 0 3.002.704.359.42.468 1.044.331 1.906zM115.434 13.075h-3.273a.567.567 0 0 0-.562.481l-.145.916-.23-.332c-.709-1.029-2.289-1.373-3.867-1.373-3.619 0-6.709 2.741-7.311 6.586-.312 1.918.131 3.752 1.219 5.031 1 1.176 2.426 1.666 4.125 1.666 2.916 0 4.533-1.875 4.533-1.875l-.146.91a.57.57 0 0 0 .564.66h2.949a.95.95 0 0 0 .938-.803l1.771-11.209a.571.571 0 0 0-.565-.658zm-4.565 6.374c-.314 1.871-1.801 3.127-3.695 3.127-.949 0-1.711-.305-2.199-.883-.484-.574-.666-1.391-.514-2.301.297-1.855 1.805-3.152 3.67-3.152.93 0 1.686.309 2.184.892.501.589.699 1.411.554 2.317zM119.295 7.23l-2.807 17.858a.569.569 0 0 0 .562.658h2.822c.469 0 .867-.34.939-.803l2.768-17.536a.57.57 0 0 0-.562-.659h-3.16a.571.571 0 0 0-.562.482zM7.266 29.154l.523-3.322-1.165-.027H1.061L4.927 1.292a.316.316 0 0 1 .314-.268h9.38c3.114 0 5.263.648 6.385 1.927.526.6.861 1.227 1.023 1.917.17.724.173 1.589.007 2.644l-.012.077v.676l.526.298a3.69 3.69 0 0 1 1.065.812c.45.513.741 1.165.864 1.938.127.795.085 1.741-.123 2.812-.24 1.232-.628 2.305-1.152 3.183a6.547 6.547 0 0 1-1.825 2c-.696.494-1.523.869-2.458 1.109-.906.236-1.939.355-3.072.355h-.73c-.522 0-1.029.188-1.427.525a2.21 2.21 0 0 0-.744 1.328l-.055.299-.924 5.855-.042.215c-.011.068-.03.102-.058.125a.155.155 0 0 1-.096.035H7.266z" fill="#253b80"/>
            <path d="M23.048 7.667c-.028.179-.06.362-.096.55-1.237 6.351-5.469 8.545-10.874 8.545H9.326c-.661 0-1.218.48-1.321 1.132L6.596 26.83l-.399 2.533a.704.704 0 0 0 .695.814h4.881c.578 0 1.069-.42 1.16-.99l.048-.248.919-5.832.059-.32c.09-.572.582-.992 1.16-.992h.73c4.729 0 8.431-1.92 9.513-7.476.452-2.321.218-4.259-.978-5.622a4.67 4.67 0 0 0-1.336-1.03z" fill="#179bd7"/>
            <path d="M21.754 7.151a9.757 9.757 0 0 0-1.203-.267 15.284 15.284 0 0 0-2.426-.177h-7.352a1.172 1.172 0 0 0-1.159.992L8.05 17.605l-.045.289a1.336 1.336 0 0 1 1.321-1.132h2.752c5.405 0 9.637-2.195 10.874-8.545.037-.188.068-.371.096-.55a6.594 6.594 0 0 0-1.017-.429 9.045 9.045 0 0 0-.277-.087z" fill="#222d65"/>
            <path d="M9.614 7.699a1.169 1.169 0 0 1 1.159-.991h7.352c.871 0 1.684.057 2.426.177a9.757 9.757 0 0 1 1.481.353c.365.121.704.264 1.017.429.368-2.347-.003-3.945-1.272-5.392C20.378.682 17.853 0 14.622 0h-9.38c-.66 0-1.223.48-1.325 1.133L.01 25.898a.806.806 0 0 0 .795.932h5.791l1.454-9.225 1.564-9.906z" fill="#253b80"/>
        </svg>
    );
}
